import React, { useState, useEffect, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { Loader2, Eye, Users, TrendingUp, FileText, Globe } from "lucide-react";

const RANGES = [
  { v: "7d", label: "7 Days" },
  { v: "30d", label: "30 Days" },
  { v: "all", label: "All Time" },
];

const Stat = ({ icon: Icon, label, value }) => (
  <div className="card-surface rounded-xl p-5" data-testid={`analytics-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="font-data text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <Icon className="h-4 w-4 text-brand" />
    </div>
    <div className="font-heading text-3xl font-black mt-2">{value}</div>
  </div>
);

const prettyPath = (p) => (p === "/" ? "/ (Home)" : p);

export const AdminAnalytics = () => {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/analytics/summary?range=${r}`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(range); }, [range, load]);

  const maxPageViews = data?.top_pages?.[0]?.views || 1;
  const maxSourceViews = data?.top_sources?.[0]?.views || 1;

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <p className="text-sm text-slate-400 max-w-xl">Live website traffic — page views, unique visitors and where they come from.</p>
        <div className="flex items-center gap-1.5 bg-[#0C1017] border border-white/10 rounded-lg p-1">
          {RANGES.map((r) => (
            <button
              key={r.v}
              data-testid={`analytics-range-${r.v}`}
              onClick={() => setRange(r.v)}
              className={`px-3 py-1.5 rounded-md font-data text-[11px] uppercase tracking-wider transition-colors ${range === r.v ? "bg-brand text-white" : "text-slate-400 hover:text-white"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-24 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : !data ? (
        <div className="card-surface rounded-xl py-16 text-center text-sm text-slate-400">Could not load analytics.</div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat icon={Eye} label="Page Views" value={data.total_views.toLocaleString()} />
            <Stat icon={Users} label="Unique Visitors" value={data.unique_visitors.toLocaleString()} />
            <Stat icon={TrendingUp} label="Views Today" value={data.views_today.toLocaleString()} />
          </div>

          <div className="card-surface rounded-xl p-5">
            <h3 className="font-heading text-lg font-bold mb-4">Traffic Over Time</h3>
            {data.series.length === 0 ? (
              <p className="text-sm text-slate-500 py-12 text-center">No traffic recorded yet in this range.</p>
            ) : (
              <div className="h-72" data-testid="analytics-traffic-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2962FF" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#2962FF" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="guv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00D4AA" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#00D4AA" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} tickFormatter={(d) => (d || "").slice(5)} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
                    <Tooltip contentStyle={{ background: "#0C1017", border: "1px solid rgba(148,163,184,0.2)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#94A3B8" }} />
                    <Area type="monotone" dataKey="views" name="Page Views" stroke="#2962FF" strokeWidth={2} fill="url(#gv)" />
                    <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#00D4AA" strokeWidth={2} fill="url(#guv)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-surface rounded-xl p-5">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><FileText className="h-5 w-5 text-brand" /> Top Pages</h3>
              <div className="space-y-2.5" data-testid="analytics-top-pages">
                {data.top_pages.length === 0 ? <p className="text-sm text-slate-500">No data.</p> : data.top_pages.map((p, i) => (
                  <div key={i} className="relative">
                    <div className="flex justify-between items-center text-sm mb-1"><span className="font-data text-slate-200 truncate pr-3">{prettyPath(p.path)}</span><span className="font-data text-slate-400">{p.views}</span></div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-brand" style={{ width: `${(p.views / maxPageViews) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-surface rounded-xl p-5">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><Globe className="h-5 w-5 text-brand" /> Top Sources</h3>
              <div className="space-y-2.5" data-testid="analytics-top-sources">
                {data.top_sources.length === 0 ? <p className="text-sm text-slate-500">No data.</p> : data.top_sources.map((s, i) => (
                  <div key={i} className="relative">
                    <div className="flex justify-between items-center text-sm mb-1"><span className="font-data text-slate-200 truncate pr-3">{s.source}</span><span className="font-data text-slate-400">{s.views}</span></div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-gain" style={{ width: `${(s.views / maxSourceViews) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
